import { action, computed, makeObservable, observable } from 'mobx';
import { TKiosk, kioskCast } from '../../../types/kiosks';
import { defaultKiosksFilters } from '../constants/kiosks';
import { IKiosksFilters } from '../types/kiosks';
import { AVAILABILITY_OPTIONS, defaultPagination } from '~/constants/main';
import { SCREENS } from '~/constants/screens';
import { getAbortController, isCanceledRequest } from '~/helpers';
import { modalStore } from '~/mobx/modalStore';
import client from '~/services/client';
import { history } from '~/services/history';
import logger from '~/services/logger';
import { IPagination, LoadingStatus, TBooleanInNumber } from '~/types/main';
import { booleanNumberReverse } from '~/utils/helpers';

class KiosksStore {
  constructor() {
    makeObservable(this);
  }

  private getKiosksAbortController = getAbortController();
  private cancelStack: Record<string, AbortController> = {};

  @observable public pagination: IPagination<TKiosk> = defaultPagination;
  @observable public filters: IKiosksFilters = defaultKiosksFilters;

  @observable public loading: LoadingStatus = LoadingStatus.Init;

  @computed public get isLoading(): boolean {
    return [LoadingStatus.Loading, LoadingStatus.Refreshing].includes(this.loading);
  }

  @computed public get isListOfDeleted(): boolean {
    return this.pagination.data.some((kiosk) => kiosk.isDelete);
  }

  @action public changeFilters = (filters: Partial<IKiosksFilters>) => {
    this.filters = { ...this.filters, ...filters };
    this.fetchKiosks();
  };

  @action public fetchKiosks = async (page: number = 1) => {
    this.getKiosksAbortController.abort();
    this.getKiosksAbortController = getAbortController();

    try {
      this.loading = LoadingStatus.Loading;

      const params: any = {
        page,
        search: this.filters.search,
        sort_field: this.filters.sortField || undefined,
        sort_dir: this.filters.sortDir || undefined,
        branch: this.filters.branchOption?.value,
        is_delete: 0,
      };

      if (this.filters.statusOption?.value === AVAILABILITY_OPTIONS.DELETED) {
        params.is_delete = 1;
      } else {
        params.status = this.filters.statusOption?.value;
      }

      const pagination = await client.get('v4/kiosks', { params, signal: this.getKiosksAbortController.signal });

      this.pagination = { ...pagination, data: pagination.data.map(kioskCast) };
      this.loading = LoadingStatus.Success;
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.loading = LoadingStatus.Success;
        return;
      }

      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'kiosksStore fetchKiosks' });
    }
  };

  @action public updateKioskStatus = async (kioskId: string, status: TBooleanInNumber) => {
    const cancelId = `kiosk-status_${kioskId}`;

    try {
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId].abort();
      this.cancelStack[cancelId] = getAbortController();

      this.pagination.data = this.pagination.data.map((item) => (item.id === kioskId ? { ...item, status } : item));

      await client.post(`v4/kiosks/${kioskId}/change-status`, { status }, { signal: this.cancelStack[cancelId].signal });
    } catch (error: any) {
      if (isCanceledRequest(error)) {
        this.loading = LoadingStatus.Success;
        return;
      }

      this.pagination.data = this.pagination.data.map((item) =>
        item.id === kioskId ? { ...item, status: booleanNumberReverse(item.status) } : item
      );
      logger.error({ error, place: 'kiosksStore updateKioskStatus' });
    }
  };

  @action public deleteKiosk = async (kioskId: string) => {
    try {
      this.loading = LoadingStatus.Loading;

      await client.delete(`v4/kiosks/${kioskId}`);
      await this.fetchKiosks(this.pagination.current_page);
    } catch (error: any) {
      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'kiosksStore deleteKiosk' });
    }
  };

  @action public restoreKiosk = async (kioskId: string) => {
    try {
      this.loading = LoadingStatus.Loading;

      const response = await client.post(`v4/kiosks/${kioskId}/restore`);

      // handle kiosks deleted branch error
      if (response.success) {
        await this.fetchKiosks(this.pagination.current_page);
      } else {
        modalStore.openErrorModal({ message: response.message });
        this.loading = LoadingStatus.Error;
      }
    } catch (error: any) {
      this.loading = LoadingStatus.Error;
      logger.error({ error, place: 'kiosksStore restoreKiosk' });
    }
  };

  public clearFilters = () => {
    if (!history.location.pathname.includes(SCREENS.SETTINGS_DEVICES_KIOSKS())) this.filters = defaultKiosksFilters;
  };
}

const kiosksStore = new KiosksStore();

export default kiosksStore;
